import { createFileRoute } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect } from 'react'
import { usePostLoginRedirect } from '@/hooks/use-post-login-redirect'
import { endLogout } from '@/lib/logout-signal'
import { useLingui } from '@lingui/react/macro'
import { useAppForm } from '@/lib/use-app-form'
import { useAppStore } from '@/lib/app-store'
import { clearQueryClient, queryClient, trpc } from '@/main'
import { SocialButtons } from '@/routes/_app/-components/social-buttons'
import { AuthCard } from '@/routes/_app/-components/shared/auth-card'
import { AuthCardSkeleton } from '@/routes/_app/-components/app-layout-skeleton'
import { Separator } from '@/components/ui/separator'
import { resolveRedirectTarget } from '@/lib/redirect-target'
import * as z from 'zod'
import * as v from 'valibot'

export const Route = createFileRoute('/_app/_public/login')({
  validateSearch: v.object({
    // Sanitize on the way in: unwrap any nested `/login?redirect=…` so a bad inbound URL can't be read
    // back out (or forwarded to the /register link) as a self-referential redirect. See resolveRedirectTarget.
    redirect: v.pipe(
      v.optional(v.string()),
      v.transform((value) => resolveRedirectTarget(value)),
    ),
  }),
  component: RouteComponent,
  pendingComponent: () => <AuthCardSkeleton logo social forgot />,
})

function RouteComponent() {
  const { redirect } = Route.useSearch()
  const { t } = useLingui()
  const redirectAfterLogin = usePostLoginRedirect()
  const { mutateAsync: login } = useMutation(trpc.auth.login.mutationOptions())

  // Logout is now a client navigation (not a full reload), so clear the "logging out" signal once we
  // land here — otherwise it would persist into the next session and leave the topbar stuck neutral.
  // Safe on this bare /login page: the topbar isn't mounted, so resetting causes no flash.
  useEffect(() => endLogout(), [])

  // No auth guard here on purpose: a logged-in user can reach /login to switch accounts ("Switch to
  // account" in the user menu). Social login now resolves its destination server-side and redirects
  // straight there (shared/post-login), so it never bounces back through /login. The password flow
  // redirects via usePostLoginRedirect on submit.

  const form = useAppForm({
    // Validate on submit only (not onChange). On mobile, autofill/keyboard interactions can flip a
    // field to "touched" the moment it's focused; with onChange validation that surfaced an
    // "invalid email" error before the user had typed anything. onSubmit keeps the field clean until
    // the user actually tries to log in — the standard login-form UX.
    validators: {
      onSubmit: z.object({
        email: z.email(),
        password: z.string().min(1),
      }),
    },
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      const result = await login(value)
      // Drop ALL cached data from the pre-login state before navigating. We log in via a client nav
      // (not the full reload this flow used to do), so otherwise queries cached for the prior state
      // survive against the new session: `getMyEmployers` was prefetched empty on the logged-out
      // /login page (authOptional → []) so the employer switcher renders nothing, and a stale
      // `getMyEmployeeProfile` snapshot mixes a previous employer's avatar/name into the user menu —
      // both only corrected on a manual refresh. Clearing makes the client nav behave like that reload.
      clearQueryClient()
      // Re-arm the welcome-guide "Close" latch on every fresh login. It's an in-memory latch that only
      // resets on a full page reload; because login is a client navigation (no reload), a session that
      // had closed the guide with "Close" would otherwise stay latched into the next login, making
      // "Close" behave like "Don't show again". Resetting here means "Close" reopens the guide on the
      // next entry (while tour === 0), while "Don't show again" (tour = 1) still suppresses it.
      useAppStore.getState().resetWelcome()
      // Re-seed the user cache with the just-authenticated user so the client navigation below reads
      // the logged-in state immediately (a client nav otherwise reads the now-empty `getUser` and
      // bounces back to /login).
      queryClient.setQueryData(trpc.auth.getUser.queryKey(), result)
      redirectAfterLogin(result)
    },
  })

  return (
    <AuthCard
      home
      title={t`Welcome back`}
      subtitle={t`Sign in to your D'Anchiano account`}
      contentClassName="flex flex-col gap-5"
      footer={
        <>
          {t`Don't have an account?`}{' '}
          <Link to="/register" search={{ redirect }} className="text-primary font-medium hover:underline">
            {t`Sign up`}
          </Link>
        </>
      }
    >
      <SocialButtons login />

      <div className="flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-muted-foreground text-xs">{t`or`}</span>
        <Separator className="flex-1" />
      </div>

      <form
        // noValidate: type="email" would otherwise trigger the browser's native (English,
        // unstyled) validation bubble on submit. We let the zod onSubmit validator own it so the
        // error is the app's styled, localized field message instead.
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          form.handleSubmit()
        }}
        className="flex flex-col gap-3"
      >
        <form.AppField
          name="email"
          children={(field) => (
            <field.TextField label={t`Email`} type="email" inputMode="email" autoComplete="username" />
          )}
        />

        <form.AppField
          name="password"
          children={(field) => <field.PasswordField label={t`Password`} autoComplete="current-password" />}
        />

        <Link to="/begin-password-reset" className="text-muted-foreground text-sm hover:underline">
          {t`Forgot your password?`}
        </Link>

        <form.AppForm>
          <form.SubmitButton label={t`Login`} />
        </form.AppForm>
      </form>
    </AuthCard>
  )
}
